import getContext from './utils/getContext';

const context = getContext();
const CONTEXT_REGION = context?.regionCode || context?.region || 'en-us';
const URL_REGION = CONTEXT_REGION === 'en-us' ? '' : `/${CONTEXT_REGION}`; // Some URLs for US region don't require /en-us/ in them
export const BASE_URL = context?.settings?.siteUrl || 'https://www.23andme.org';
export const PERMALINKS_URL = context?.settings?.permalinksUrl || 'https://permalinks.23andme.com';
// Bare hostname derived from BASE_URL — for inline display where we don't want
// to render the scheme (e.g. paragraph copy referencing the public site).
// Uses `.hostname` (not `.host`) so a port in BASE_URL can't leak into copy.
export const SITE_HOST = new URL(BASE_URL).hostname;
const CUSTOMER_CARE = 'customercare.23andme.com/hc/en-us';
// Helper to safely join URL parts using the URL constructor (handles trailing/leading slashes)
const joinUrl = (base: string, path: string) => new URL(path, base).href;

// Helper to build region-prefixed URLs (US region has no prefix for some 23andme URLs)
const getRegionPrefix = (region: Region) => (region === 'en-us' ? '' : `/${region}`);
export const buildRegionUrl = (path: string, region: Region = CONTEXT_REGION) =>
  joinUrl(BASE_URL, `${getRegionPrefix(region)}${path}`);

// Mapbox Address Autofill token - configured via environment
// Returns undefined if not configured, which gracefully disables autofill
export const MAPBOX_ACCESS_TOKEN: string | undefined = context?.settings?.mapboxToken;

export const isApplepayMerchantTokenEnabled = (): boolean =>
  context?.settings?.enableApplepayMerchantToken === true;

export const REGIONS = {
  US: 'en-us',
  CA: 'en-ca',
  GB: 'en-gb',
  EU: 'en-eu',
  INT: 'en-int',
};

export const REGION_CODE = `${CONTEXT_REGION}`;
export const REGION = `${CONTEXT_REGION}`.replace('-', '');
export const SINGLE_COUNTRY_REGIONS = ['enus', 'enca', 'engb'];
export const COUNTRY_TO_REGION_MAP = {
  US: 'enus',
  CA: 'enca',
  GB: 'engb',
  AS: 'enus',
  MP: 'enus',
  PR: 'enus',
  VI: 'enus',
  GU: 'enus',
  DK: 'eneu',
  FI: 'eneu',
  IE: 'eneu',
  NL: 'eneu',
  SE: 'eneu',
};

// Mapbox AddressAutofill country restrictions per region
// ISO 3166-1 alpha-2 codes, comma-separated for multiple countries
export const MAPBOX_COUNTRIES: Record<string, string | undefined> = {
  enus: 'US',
  enca: 'CA',
  engb: 'GB',
  // All 27 EU member states
  eneu: 'AT,BE,BG,HR,CY,CZ,DK,EE,FI,FR,DE,GR,HU,IE,IT,LV,LT,LU,MT,NL,PL,PT,RO,SK,SI,ES,SE',
  // INT: undefined means no restriction (Mapbox doesn't support exclusion)
  enint: undefined,
};
export const COOKIE_BANNER_REGIONS =
  []; /* originally ['engb', 'eneu'] - may re-enable if necessary */
export const GDPR_REGIONS = ['engb', 'eneu', 'enint'];
export const PRICE_MODAL_REGIONS = ['enus'];
export const PRICE_MODAL_KIT_SKUS = [
  'US-KIT-PGS',
  'US-KIT-HLH-V0',
  'US-KIT-HLH-V1',
  'US-KIT-HLH-V2',
];
export const HEAP_REGIONS = ['enca', 'enus', 'engb'];
export const HEAP_SKUS = {
  ANCESTRY: ['CA-KIT-ANC', 'US-KIT-V4-ANC-2016', 'US-KIT-ANC-V0', 'US-KIT-ANC-V1', 'US-KIT-ANC-V2'],
  HEALTH: ['CA-KIT-PGS', 'US-KIT-PGS', 'US-KIT-HLH-V0', 'US-KIT-HLH-V1', 'US-KIT-HLH-V2'],
};
export const TOTAL_HEALTH_ALIAS = 'us_exome_health_subs_12_mo_prepaid';
export const PERMITTED_REGIONS = {
  ALL: ['us', 'ca', 'gb', 'eu', 'int'],
  US: ['us'],
};
export const COUNTRIES = {
  US: 'US',
  GDPR_REGIONS: ['US', 'CA', 'GB'],
};
export const getPgtPermalink = (region: Region) =>
  buildRegionUrl('/shop/test-info/pharmacogenetics', region);
export const getFdaInfo = (region: Region) => buildRegionUrl('/shop/test-info/', region);
export const getSubscriptionTermsOfService = (region: Region) =>
  buildRegionUrl('/legal/terms-of-service/subscription', region);
export const URLS = {
  AD_CHOICES: joinUrl(BASE_URL, `${URL_REGION}/about/cookies/#first-third-party-cookies`),
  AUTHENTICATION: `/${CONTEXT_REGION}/authenticate/`,
  CART: `/${CONTEXT_REGION}/cart/`,
  CONSUMER_HEALTH_DATA_PRIVACY_POLICY: joinUrl(
    BASE_URL,
    '/legal/us-privacy/#washington-consumer-health-data-privacy-policy',
  ),
  CUSTOMER_CARE: `https://${CUSTOMER_CARE}`,
  CONSENT_DOC: joinUrl(BASE_URL, `${URL_REGION}/legal/consent/`),
  COOKIES: joinUrl(BASE_URL, `${URL_REGION}/about/cookies/`),
  COOKIE_CHOICES: `#cookie-choices`,
  DATA_PROTECTION: joinUrl(BASE_URL, `${URL_REGION}/gdpr`),
  FAMILY_CONSIDERATIONS: `https://${CUSTOMER_CARE}/articles/202907980`,
  FDA_INFO: getFdaInfo(CONTEXT_REGION),
  FETCH_LABELS: `/${CONTEXT_REGION}/labels/`,
  FETCH_SHIPPING_METHODS: `/${CONTEXT_REGION}/shipping_methods/`,
  FETCH_STATES: `/${CONTEXT_REGION}/states/`,
  FSA_HSA: joinUrl(BASE_URL, '/shop/fsa-hsa/'),
  GIFTWRAP: `/${CONTEXT_REGION}/giftwrap`,
  HOME: joinUrl(BASE_URL, URL_REGION || '/'),
  PGT_PERMALINK: getPgtPermalink(CONTEXT_REGION),
  PRIVACY_POLICY: joinUrl(BASE_URL, `${URL_REGION}/legal/privacy`),
  TELEHEALTH_TOS: joinUrl(BASE_URL, `${URL_REGION}/legal/telehealth-tos`),
  RECEIPT: `/${CONTEXT_REGION}/receipt/`,
  LABS_RECEIPT: `/${CONTEXT_REGION}/labs/receipt/`,
  TOTAL_HEALTH_EXISTING_USER_SIGN_UP: joinUrl(BASE_URL, '/shop/total-health/#sign-up'),
  REFUND_POLICY: {
    enca: `https://ca.${CUSTOMER_CARE}/articles/115000268708`,
    eneu: `https://eu.${CUSTOMER_CARE}/articles/204704750`,
    engb: `https://eu.${CUSTOMER_CARE}/articles/204704750`,
    enint: `https://int.${CUSTOMER_CARE}/articles/214810808`,
    enus: `https://${CUSTOMER_CARE}/articles/202907780`,
  },
  CUSTOMER_CARE_LANDING: {
    enca: 'https://support.23andme.org/hc/en-ca/',
    eneu: 'https://support.23andme.org/hc/en-150/',
    engb: 'https://support.23andme.org/hc/en-gb/',
    enint: 'https://support.23andme.org/hc/en-001/',
    enus: 'https://support.23andme.org/hc/en-us/',
  },
  PREMIUM_ABOUT: {
    enca: 'https://support.23andme.org/hc/en-ca/articles/42956097469591-Premium-Memberships',
    eneu: 'https://support.23andme.org/hc/en-150/articles/42956097469591-Premium-Memberships',
    engb: 'https://support.23andme.org/hc/en-gb/articles/42956097469591-Premium-Memberships',
    enint: 'https://support.23andme.org/hc/en-001/articles/42966311919383-About-Premium-Ancestry',
    enus: 'https://support.23andme.org/hc/en-us/articles/42956097469591-Premium-Memberships',
  },
  SAVED_PAYMENT: `/${CONTEXT_REGION}/saved-payments/`,
  SURVEY: `/${CONTEXT_REGION}/checkout/survey/`,
  SHIPMETHOD: `/${CONTEXT_REGION}/checkout/shipmethod/`,
  SHIPPING: `/${CONTEXT_REGION}/checkout/shipping/`,
  SITE_MAP: joinUrl(BASE_URL, `${URL_REGION}/sitemap`),
  SUBSCRIPTION_RECEIPT: `/${CONTEXT_REGION}/subscription/receipt/`,
  SAVED_PAYMENTS: `/${CONTEXT_REGION}/saved-payments/`,
  SUBSCRIPTION_RENEWAL: `/${CONTEXT_REGION}/renew-subscription/payment/`,
  TERMS_OF_SERVICE: joinUrl(BASE_URL, `${URL_REGION}/legal/terms-of-service`),
  SUBSCRIPTION_TERMS_OF_SERVICE: getSubscriptionTermsOfService(CONTEXT_REGION),
  VALIDATION: `/${CONTEXT_REGION}/checkout/validation/`,
  YOUDOT: context?.settings?.youdotUrl,
};
export const POLICY_LINKS = {
  OTHER_STORE_FAQS: `https://${CUSTOMER_CARE}/sections/202505318`,
  TERMS_OF_SERVICE: URLS.TERMS_OF_SERVICE,
  REFUND_POLICY: URLS.REFUND_POLICY[REGION],
  CUSTOMER_CARE: `https://${CUSTOMER_CARE}`,
  PRIVACY_STATEMENT: URLS.PRIVACY_POLICY,
  FAMILY_CONSIDERATIONS: URLS.FAMILY_CONSIDERATIONS,
  COOKIE_POLICY: URLS.COOKIES,
  COOKIE_CHOICES: '#cookie-choices',
  DATA_PROTECTION: URLS.DATA_PROTECTION,
  MEMBERSHIP_TERMS: URLS.SUBSCRIPTION_TERMS_OF_SERVICE,
  ...(CONTEXT_REGION === 'en-us'
    ? {
        IMPORTANT_TEST_INFO: URLS.FDA_INFO,
        FSA_HSA: URLS.FSA_HSA,
      }
    : {}),
};
export const SALE_AND_USE_COUNTRY = {
  enca: 'Canada',
  eneu: 'Denmark, Finland, Ireland, Sweden and the Netherlands',
  engb: 'the United Kingdom',
  enint: 'the country to which the kit is shipped from 23andMe',
  enus: 'the US',
};
export const WRONG_COUNTRY_ERROR = {
  enca: {
    desiredCountry: '',
    site: 'Canadian',
    urlSuffix: 'ca',
  },
  eneu: {
    desiredCountry: '',
    site: 'European Union',
    urlSuffix: 'eu',
  },
  engb: {
    desiredCountry: 'England, Scotland, Wales or Northern Ireland',
    site: 'UK',
    urlSuffix: 'co.uk',
  },
  enus: {
    desiredCountry: 'the United States',
    site: 'United States',
    urlSuffix: 'com',
  },
  enint: {
    desiredCountry: '',
    site: 'International',
    urlSuffix: 'int',
  },
};
